import React, { useContext, useMemo } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';

import GridCellErrorBoundary from 'components/GridCellErrorBoundary';
import Cell from 'components/V5Gadget/Matrix/Cell';
import Row from 'components/Common/Row';
import Tooltip from 'components/Tooltip';
import { BonusAppContext } from 'contextProviders/BonusAppProvider';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import useValueFormatters from 'hooks/useValueFormatters';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import captureException from 'services/captureException';

import useBonusCellBonusProgram from '../hooks/useBonusCellBonusProgram';

export const StatusIcon = ({ passed }: { passed: boolean }) => {
  if (passed) {
    return <Icon icon="checkmark" color={'var(--color-success-v2)'} />;
  }

  return <Icon icon="cross" color={'var(--color-status-error)'} />;
};

export const TooltipExpectationSection = ({
  expectation,
  elasticDocument,
  dataset,
}: {
  expectation: BonusTypes.StatusFieldExpectation;
  elasticDocument: ElasticDocument;
  dataset: string;
}) => {
  const { getFieldLabel } = useGetFieldLabel();
  const { formatField } = useValueFormatters();

  const actual = useMemo(() => {
    try {
      const raw = elasticDocument[expectation.field];
      const formatted = formatField({
        field: expectation.field,
        value: raw,
        dataset,
      });

      return {
        raw,
        formatted,
      };
    } catch (ex) {
      const error = new Error();
      error.name = 'TooltipExpectation: Failed to format actual';
      captureException(error);
      return undefined;
    }
  }, [dataset, elasticDocument, expectation.field, formatField]);

  const expected = useMemo(() => {
    try {
      const raw = expectation.expectation;
      const formatted = formatField({
        field: expectation.field,
        value: raw,
        dataset,
      });

      return {
        raw,
        formatted,
      };
    } catch (ex) {
      const error = new Error();
      error.name = 'TooltipExpectation: Failed to format expectation';
      captureException(error);
      return undefined;
    }
  }, [dataset, expectation.expectation, expectation.field, formatField]);

  const fieldLabel = useMemo(() => {
    try {
      return getFieldLabel({ field: expectation.field, dataType: dataset });
    } catch (ex) {
      const error = new Error();
      error.name = 'TooltipExpectation: Failed to format field label';
      captureException(error);
      return expectation.field;
    }
  }, [dataset, expectation.field, getFieldLabel]);

  if (!actual || !expected) {
    return null;
  }

  return (
    <Row centerAlign spaceBetween>
      <Row centerAlign style={{ gap: 8, marginRight: 32 }}>
        <StatusIcon passed={expectation.passed} />
        <Typography.Body type={'Button Text'}>{fieldLabel}</Typography.Body>
      </Row>
      <Row style={{ justifyContent: 'flex-end' }}>
        <Typography.Body type={'Button Text'}>
          {actual.formatted}
        </Typography.Body>
        <div style={{ marginLeft: 4, marginRight: 4 }}>
          <Typography.Body type={'Button Text'}>/</Typography.Body>
        </div>
        <Typography.Body type={'Button Text'}>
          {expected.formatted}
        </Typography.Body>
      </Row>
    </Row>
  );
};

export const BonusTooltipContent = ({
  elasticDocument,
  expectation,
  dataset,
}: {
  elasticDocument: ElasticDocument;
  expectation: BonusTypes.StatusFieldExpectation;
  dataset: string;
}) => {
  return (
    <div style={{ gap: 8 }}>
      <TooltipExpectationSection
        dataset={dataset}
        elasticDocument={elasticDocument}
        expectation={expectation}
      />
    </div>
  );
};

const BonusStatusCell = ({
  bonusProgram,
  expectation,
  elasticDocument,
}: {
  bonusProgram: BonusTypes.BonusProgram;
  expectation: BonusTypes.StatusFieldExpectation;
  elasticDocument: ElasticDocument;
}) => {
  return (
    <Cell>
      <Tooltip
        isAltTooltip
        content={
          <BonusTooltipContent
            elasticDocument={elasticDocument}
            dataset={bonusProgram.dataset}
            expectation={expectation}
          />
        }
      >
        <StatusIcon passed={expectation.passed} />
      </Tooltip>
    </Cell>
  );
};

/**
 * Shows the status of a given expectation,
 * eg: for the field totalMilesStatus, show a green tick
 */
const BonusStatusCellProvider = (params: ICellRendererParams) => {
  const bonusAppContext = useContext(BonusAppContext);
  const bonusProgram = useBonusCellBonusProgram();
  const elasticDocument = useMemo(() => {
    return params.node.data as ElasticDocument;
  }, [params.node.data]);

  const field = useMemo(() => {
    const { colDef } = params;
    if (!colDef || !colDef.field) {
      return undefined;
    }

    return colDef.field;
  }, [params]);

  const criteriaField = useMemo(() => {
    if (!bonusProgram || !field) {
      return undefined;
    }

    const expectationField = bonusProgram.criteriaFields.find(
      (f) => f.statusField === field,
    );
    if (!expectationField) {
      return undefined;
    }
    return expectationField;
  }, [bonusProgram, field]);

  const status = useMemo((): 'Passed' | 'Failed' | undefined => {
    if (!criteriaField) {
      return undefined;
    }

    return elasticDocument[criteriaField.statusField];
  }, [criteriaField, elasticDocument]);

  const isPassed = useMemo((): boolean => {
    if (!status) {
      return false;
    }

    return status === 'Passed';
  }, [status]);

  const expectation = useMemo(():
    | BonusTypes.StatusFieldExpectation
    | undefined => {
    if (!field || !bonusProgram) {
      return undefined;
    }
    const criteriaField = bonusProgram.criteriaFields.find(
      (f) => f.statusField === field,
    );
    if (!criteriaField) {
      return undefined;
    }

    const baseMetaData: BonusTypes.StatusFieldExpectation = {
      field: criteriaField.actualField,
      expectation: elasticDocument[criteriaField.targetField],
      passed: isPassed,
    };

    if (
      bonusAppContext &&
      bonusAppContext.demoOverridenStatusFields[elasticDocument.id] !==
        undefined
    ) {
      baseMetaData.passed =
        bonusAppContext.demoOverridenStatusFields[elasticDocument.id];
    }

    return baseMetaData;
  }, [bonusAppContext, bonusProgram, elasticDocument, field, isPassed]);

  if (!bonusProgram || !expectation || params.node.isRowPinned()) {
    return <Cell />;
  }

  return (
    <BonusStatusCell
      bonusProgram={bonusProgram}
      expectation={expectation}
      elasticDocument={elasticDocument}
    />
  );
};

const Gate = (params: ICellRendererParams) => (
  <GridCellErrorBoundary
    params={params}
    childComponent={<BonusStatusCellProvider {...params} />}
  />
);

export default Gate;
